import { useCallback } from 'react';
import { AppState } from 'react-native';
import Modal from 'utils/packages/Modal';
import { useAppDispatch, useAppStore } from 'store/hooks';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { selectPasscodeVisible } from 'reducers/selectors';
import { setSelectedSpace } from '../actions';
import { selectSelectedSpace } from '../reducers/selectors';
// Settle delay before showing the confirm modal, so it doesn't animate in over
// the passcode-lock fade-out (~300ms, ModalLockContainer) or the app-resume
// transition when returning from the background.
const LOCK_FADE_OUT_MS = 300;
/**
 * Shared space-switch flow for notification/alert taps (push + in-feed).
 *
 * If the notification belongs to a different space than the current one (and
 * the user has access to it), this asks the user to confirm the switch. On
 * confirm it switches space; the caller then proceeds to the deep-link target.
 *
 * The confirm step waits until the app is ready — unlocked (passcode entered)
 * and in the foreground — so the modal never appears over the lock screen or
 * the app-resume transition on a cold-start / backgrounded push tap. (No-op
 * when already active + unlocked, e.g. an in-feed tap.)
 *
 * Returns a promise resolving to whether the caller should PROCEED to the
 * target:
 *  - `true`  → no switch needed (same space / no space context / no access),
 *              or the user confirmed the switch (which was performed).
 *  - `false` → a switch was needed but the user declined; do not navigate.
 *
 * After a confirmed switch, the "App changed to: X" success screen is shown.
 * The promise then resolves `true` only once the user dismisses that screen
 * (via its "Done" button), so the caller still follows the deep-link to the
 * target — just after the success screen.
 */
export default function useNotificationSpaceSwitch() {
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const navigation = useMainStackNavigation();
    // Resolves once the app is ready to show the modal: unlocked (passcode gone)
    // AND in the foreground. After any required wait (lock dismissal or returning
    // from the background) it adds a short settle delay so the modal doesn't
    // animate in over the lock fade / resume transition. Resolves immediately
    // with no delay when already active + unlocked (e.g. an in-feed tap).
    const waitUntilReady = useCallback(() => {
        const isReady = () => !selectPasscodeVisible(store.getState()) &&
            AppState.currentState === 'active';
        if (isReady()) {
            return Promise.resolve();
        }
        return new Promise((resolve) => {
            let done = false;
            let unsubscribeStore = () => { };
            let appStateSub;
            const finish = () => {
                if (done || !isReady())
                    return;
                done = true;
                unsubscribeStore();
                appStateSub?.remove();
                setTimeout(resolve, LOCK_FADE_OUT_MS);
            };
            unsubscribeStore = store.subscribe(finish);
            appStateSub = AppState.addEventListener('change', finish);
            // In case it became ready between the check above and subscribing.
            finish();
        });
    }, [store]);
    return useCallback((data) => {
        // No space context → nothing to switch, proceed.
        if (!data || !('spaceId' in data) || typeof data.spaceId !== 'number') {
            return Promise.resolve(true);
        }
        const state = store.getState();
        const targetSpaceId = data.spaceId;
        // Already in the right space → proceed.
        if (selectSelectedSpace(state) === targetSpaceId) {
            return Promise.resolve(true);
        }
        // No access to the target space → can't switch; let the deep-link guard
        // handle it (proceed without switching).
        const targetInfo = Object.values(state.spaces.spaceInfo).find((info) => info.space.id === targetSpaceId);
        if (!targetInfo) {
            return Promise.resolve(true);
        }
        const { isDefault, name } = targetInfo.space;
        const spaceLabel = isDefault ? 'Private Space' : `${name} Space`;
        // A switch is needed → wait until the app is ready (unlocked + foreground)
        // before showing the confirm modal
        return waitUntilReady().then(() => new Promise((resolve) => {
            Modal.showConfirmation({
                title: `This happened in ${spaceLabel}`,
                subtitle: `Do you want to switch to ${spaceLabel} to see what happened?`,
                confirmButtonTitle: 'Yes, switch space',
                cancelButtonTitle: 'No, thanks',
                onConfirm: () => {
                    navigation.navigate('Success', {
                        topTitle: 'App changed to:',
                        title: `${isDefault ? 'Private' : name} space`,
                        subTitle: 'Only assets that you have in this space will be visible in Feed.',
                        buttonText: 'Done',
                        onContinue: () => {
                            dispatch(setSelectedSpace(targetSpaceId));
                            // Dismiss the success screen, then resolve so the caller follows the deep-link to the target.
                            if (navigation.canGoBack()) {
                                navigation.goBack();
                            }
                            resolve(true);
                            return true;
                        },
                    });
                },
                onPressCancel: () => resolve(false),
                onBackdropPress: () => resolve(false),
                onSwipeComplete: () => resolve(false),
                onAndroidBackButtonPress: () => resolve(false),
            });
        }));
    }, [dispatch, store, navigation, waitUntilReady]);
}
